import { useMemo, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  useAddPtoRdChangeDocVersionMutation,
  useAddPtoRdChangePositionMutation,
  useCreatePtoRdChangeDocMutation,
  useListPtoSqQuery,
  type PtoRdChangeDoc,
  type PtoRdChangeDocDetail,
  type PtoRdDocument,
  type PtoRdEditsReason,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Input, Select } from '@shared/ui/Input';
import { Modal } from '@shared/ui/Modal';
import {
  CHANGE_DOC_EXTENSIONS,
  changeDocErrors,
  changeDocFileErrors,
  changeDocMeta,
  changePositionBody,
  changePositionErrors,
  emptyChangeDocForm,
  fileSizeText,
  formValues,
  qtyText,
  todayIso,
  valueForm,
  type ChangeDocForm,
  type ChangePositionForm,
} from './logic';
import {
  Buttons,
  CheckLabel,
  CommentField,
  Done,
  ErrorText,
  Errors,
  FieldLabel,
  Form,
  Grid,
  Muted,
  Section,
  ValueFields,
  Warn,
} from './ui';

const ACCEPT = CHANGE_DOC_EXTENSIONS.map((e) => `.${e}`).join(',');

const SetList = styled.div`
  max-height: 180px;
  overflow: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-weight: 400;
`;

const Legend = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

/** Файл из поля выбора: имя и размер — для проверки до отправки. */
function fileMeta(f: File | null): { name: string; size: number } | null {
  return f ? { name: f.name, size: f.size } : null;
}

/**
 * Загрузка документа изменения РД, согласованного Заказчиком (§3.2 канона):
 * вид из списка, название как в документе, номер и дата, комплекты,
 * согласование Заказчика (ФИО, должность, дата и отметка «согласование есть в
 * файле»), обоснование крайней необходимости, файл до 20 МБ; связь с SQ — по
 * желанию. Пока обязательное не заполнено — кнопка не отправляет, а перечисляет,
 * чего не хватает.
 */
export function ChangeDocUploadDialog({
  projectId,
  documents,
  kinds,
  maxBytes,
  onClose,
  onCreated,
}: {
  projectId: string;
  documents: readonly PtoRdDocument[];
  kinds: readonly PtoRdEditsReason[];
  maxBytes: number;
  onClose: () => void;
  onCreated: (detail: PtoRdChangeDocDetail) => void;
}) {
  const [create, { isLoading }] = useCreatePtoRdChangeDocMutation();
  const { data: sqData } = useListPtoSqQuery(projectId, { skip: !projectId });
  const [form, setForm] = useState<ChangeDocForm>(emptyChangeDocForm);
  const [file, setFile] = useState<File | null>(null);
  const [shown, setShown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = todayIso();
  const full = { ...form, file: fileMeta(file) };
  const errors = changeDocErrors(full, { kinds, maxBytes, today });
  const set = <K extends keyof ChangeDocForm>(k: K, v: ChangeDocForm[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));
  const sorted = useMemo(
    () => [...documents].sort((a, b) => a.code.localeCompare(b.code, 'ru')),
    [documents],
  );

  async function submit(e: FormEvent) {
    e.preventDefault();
    setShown(true);
    setError(null);
    if (errors.length > 0 || !file) return;
    const body = new FormData();
    body.append('meta', JSON.stringify(changeDocMeta(full)));
    body.append('file', file, file.name);
    try {
      const detail = await create({ projectId, form: body }).unwrap();
      onCreated(detail);
    } catch (err) {
      setError(apiErrorText(err, 'Документ изменения не загружен.'));
    }
  }

  return (
    <Modal open title="Документ изменения РД" onClose={isLoading ? () => undefined : onClose}>
      <Form onSubmit={(e) => void submit(e)}>
        <Muted>
          Материал, которого нет в РД, вносится только по документу, согласованному Заказчиком, в
          крайней необходимости и до выдачи ревизии РД. Документ не удаляется — его можно только
          отозвать с причиной; файл заменяется новой версией, прежняя хранится.
        </Muted>
        <Grid $cols={2}>
          <FieldLabel htmlFor="cd-kind">
            Вид документа
            <Select id="cd-kind" value={form.kind} onChange={(e) => set('kind', e.target.value)}>
              <option value="">— выберите из списка —</option>
              {kinds.map((k) => (
                <option key={k.code} value={k.code}>
                  {k.label}
                </option>
              ))}
            </Select>
          </FieldLabel>
          <FieldLabel htmlFor="cd-number">
            Номер документа
            <Input
              id="cd-number"
              value={form.number}
              onChange={(e) => set('number', e.target.value)}
              maxLength={120}
            />
          </FieldLabel>
          <FieldLabel $wide htmlFor="cd-title">
            Название — как в документе
            <Input
              id="cd-title"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              maxLength={500}
            />
          </FieldLabel>
          <FieldLabel htmlFor="cd-date">
            Дата документа
            <Input
              id="cd-date"
              type="date"
              max={today}
              value={form.docDate}
              onChange={(e) => set('docDate', e.target.value)}
            />
          </FieldLabel>
          <FieldLabel htmlFor="cd-sq">
            Ответ на запрос SQ (необязательно)
            <Select
              id="cd-sq"
              value={form.sqRequestId}
              onChange={(e) => set('sqRequestId', e.target.value)}
            >
              <option value="">— без связи с SQ —</option>
              {(sqData?.items ?? []).map((q) => (
                <option key={q.id} value={q.id}>
                  {q.subject}
                </option>
              ))}
            </Select>
          </FieldLabel>
        </Grid>

        <div>
          <Legend>Комплекты РД, к которым относится документ</Legend>
          {sorted.length === 0 ? (
            <Muted>В проекте нет комплектов РД.</Muted>
          ) : (
            <SetList role="group" aria-label="Комплекты РД">
              {sorted.map((d) => (
                <CheckLabel key={d.id}>
                  <input
                    type="checkbox"
                    checked={form.documentIds.includes(d.id)}
                    onChange={(e) =>
                      set(
                        'documentIds',
                        e.target.checked
                          ? [...form.documentIds, d.id]
                          : form.documentIds.filter((x) => x !== d.id),
                      )
                    }
                  />
                  <span>
                    {d.code} · {d.title}
                  </span>
                </CheckLabel>
              ))}
            </SetList>
          )}
        </div>

        <div>
          <Legend>Согласование Заказчика</Legend>
          <Grid $cols={3}>
            <FieldLabel htmlFor="cd-approval-name">
              ФИО согласовавшего
              <Input
                id="cd-approval-name"
                value={form.approvalName}
                onChange={(e) => set('approvalName', e.target.value)}
                maxLength={200}
              />
            </FieldLabel>
            <FieldLabel htmlFor="cd-approval-position">
              Должность
              <Input
                id="cd-approval-position"
                value={form.approvalPosition}
                onChange={(e) => set('approvalPosition', e.target.value)}
                maxLength={200}
              />
            </FieldLabel>
            <FieldLabel htmlFor="cd-approval-date">
              Дата согласования
              <Input
                id="cd-approval-date"
                type="date"
                max={today}
                value={form.approvalDate}
                onChange={(e) => set('approvalDate', e.target.value)}
              />
            </FieldLabel>
          </Grid>
          <CheckLabel style={{ marginTop: 8 }}>
            <input
              type="checkbox"
              checked={form.approvalConfirmed}
              onChange={(e) => set('approvalConfirmed', e.target.checked)}
            />
            <span>Отметка согласования Заказчика есть в файле документа — я проверил.</span>
          </CheckLabel>
        </div>

        <CommentField
          value={form.urgency}
          onChange={(v) => set('urgency', v)}
          id="cd-urgency"
          label="Обоснование крайней необходимости (обязательно)"
          placeholder="Почему нельзя ждать выдачи ревизии РД: сроки монтажа, заказ длительного изготовления…"
        />
        <CommentField value={form.comment} onChange={(v) => set('comment', v)} id="cd-comment" />

        <FieldLabel htmlFor="cd-file">
          Файл документа ({CHANGE_DOC_EXTENSIONS.join(', ')}, до {fileSizeText(maxBytes)})
          <Input
            id="cd-file"
            type="file"
            accept={ACCEPT}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </FieldLabel>
        {file && (
          <Muted>
            {file.name} · {fileSizeText(file.size)}
          </Muted>
        )}

        {shown && <Errors list={errors} />}
        {error && <ErrorText>{error}</ErrorText>}
        <Buttons>
          <Button
            type="submit"
            $variant="primary"
            disabled={isLoading || (shown && errors.length > 0)}
          >
            {isLoading ? 'Загрузка…' : 'Загрузить документ'}
          </Button>
          <Button type="button" $variant="secondary" onClick={onClose} disabled={isLoading}>
            Отмена
          </Button>
        </Buttons>
      </Form>
    </Modal>
  );
}

/** Новая версия файла документа изменения: прежняя версия хранится. */
export function ChangeDocVersionDialog({
  doc,
  maxBytes,
  onClose,
}: {
  doc: PtoRdChangeDoc;
  maxBytes: number;
  onClose: () => void;
}) {
  const [addVersion, { isLoading }] = useAddPtoRdChangeDocVersionMutation();
  const [file, setFile] = useState<File | null>(null);
  const [comment, setComment] = useState('');
  const [shown, setShown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errors = changeDocFileErrors(fileMeta(file), maxBytes);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setShown(true);
    setError(null);
    if (errors.length > 0 || !file) return;
    const body = new FormData();
    body.append('file', file, file.name);
    if (comment.trim()) body.append('comment', comment.trim());
    try {
      await addVersion({ id: doc.id, form: body }).unwrap();
      onClose();
    } catch (err) {
      setError(apiErrorText(err, 'Новая версия файла не загружена.'));
    }
  }

  return (
    <Modal
      open
      title={`Новая версия файла — № ${doc.number}`}
      onClose={isLoading ? () => undefined : onClose}
    >
      <Form onSubmit={(e) => void submit(e)}>
        <Muted>
          Сейчас — версия {doc.current_version}
          {doc.file_name ? ` (${doc.file_name})` : ''}. Прежние версии хранятся и доступны для
          скачивания; реквизиты документа не меняются.
        </Muted>
        <FieldLabel htmlFor="cd-version-file">
          Файл ({CHANGE_DOC_EXTENSIONS.join(', ')}, до {fileSizeText(maxBytes)})
          <Input
            id="cd-version-file"
            type="file"
            accept={ACCEPT}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </FieldLabel>
        <CommentField
          value={comment}
          onChange={setComment}
          id="cd-version-comment"
          label="Что изменилось (необязательно)"
        />
        {shown && <Errors list={errors} />}
        {error && <ErrorText>{error}</ErrorText>}
        <Buttons>
          <Button
            type="submit"
            $variant="primary"
            disabled={isLoading || (shown && errors.length > 0)}
          >
            {isLoading ? 'Загрузка…' : 'Загрузить версию'}
          </Button>
          <Button type="button" $variant="secondary" onClick={onClose} disabled={isLoading}>
            Отмена
          </Button>
        </Buttons>
      </Form>
    </Modal>
  );
}

/** «Внести материал» по документу изменения (Б): в действующую ревизию
 *  выбранного комплекта, «ДИ №k», с обоснованием крайней необходимости. */
export function AddChangePositionForm({
  doc,
  onDone,
}: {
  doc: PtoRdChangeDoc;
  onDone: () => void;
}) {
  const [add, { isLoading }] = useAddPtoRdChangePositionMutation();
  const [form, setForm] = useState<ChangePositionForm>(() => ({
    documentId: doc.document_ids.length === 1 ? doc.document_ids[0] : '',
    value: valueForm({ name: '' }),
    docPage: '',
    docItem: '',
    urgency: doc.urgency,
    comment: '',
  }));
  const [shown, setShown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<string | null>(null);
  const errors = changePositionErrors(form);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setShown(true);
    setError(null);
    setAdded(null);
    if (errors.length > 0) return;
    try {
      const out = await add({ id: doc.id, body: changePositionBody(form) }).unwrap();
      const v = formValues(form.value).value;
      const label =
        out.position?.doc_part && typeof out.position.row_no === 'number'
          ? `${out.position.doc_part} №${out.position.row_no}`
          : 'позиция';
      setAdded(`Внесено: ${label} — «${v.name}» ${qtyText(v.quantity, v.unit)}.`);
      setShown(false);
      setForm((prev) => ({
        ...prev,
        value: valueForm({ name: '' }),
        docPage: '',
        docItem: '',
        comment: '',
      }));
    } catch (err) {
      setError(apiErrorText(err, 'Позиция не внесена.'));
    }
  }

  return (
    <Section>
      <Form onSubmit={(e) => void submit(e)}>
        <Muted>
          Только отсутствующие в РД материалы и оборудование. Позиция встанет в действующую ревизию
          комплекта с пометкой «по документу № {doc.number} (до выдачи ревизии)»; при новой ревизии
          программа сверит её с ревизией.
        </Muted>
        <Grid $cols={3}>
          <FieldLabel htmlFor={`cp-doc-${doc.id}`}>
            Комплект РД
            <Select
              id={`cp-doc-${doc.id}`}
              value={form.documentId}
              onChange={(e) => setForm((prev) => ({ ...prev, documentId: e.target.value }))}
            >
              <option value="">— комплект из документа —</option>
              {doc.document_ids.map((id, i) => (
                <option key={id} value={id}>
                  {doc.document_codes[i] ?? id}
                </option>
              ))}
            </Select>
          </FieldLabel>
          <FieldLabel htmlFor={`cp-page-${doc.id}`}>
            Страница документа
            <Input
              id={`cp-page-${doc.id}`}
              value={form.docPage}
              onChange={(e) => setForm((prev) => ({ ...prev, docPage: e.target.value }))}
              maxLength={40}
            />
          </FieldLabel>
          <FieldLabel htmlFor={`cp-item-${doc.id}`}>
            Пункт документа
            <Input
              id={`cp-item-${doc.id}`}
              value={form.docItem}
              onChange={(e) => setForm((prev) => ({ ...prev, docItem: e.target.value }))}
              maxLength={80}
            />
          </FieldLabel>
        </Grid>
        <ValueFields
          value={form.value}
          onChange={(value) => setForm((prev) => ({ ...prev, value }))}
          idPrefix={`cp-${doc.id}`}
        />
        <CommentField
          value={form.urgency}
          onChange={(urgency) => setForm((prev) => ({ ...prev, urgency }))}
          id={`cp-urgency-${doc.id}`}
          label="Обоснование крайней необходимости (обязательно)"
        />
        <CommentField
          value={form.comment}
          onChange={(comment) => setForm((prev) => ({ ...prev, comment }))}
          id={`cp-comment-${doc.id}`}
        />
        {shown && <Errors list={errors} />}
        {error && <ErrorText>{error}</ErrorText>}
        {added && <Done role="status">{added}</Done>}
        <Buttons>
          <Button
            type="submit"
            $variant="primary"
            disabled={isLoading || (shown && errors.length > 0)}
          >
            {isLoading ? 'Внесение…' : 'Внести материал'}
          </Button>
          <Button type="button" $variant="ghost" onClick={onDone}>
            Готово
          </Button>
        </Buttons>
      </Form>
    </Section>
  );
}

/** Предупреждение у документа: 30 дней действующих позиций без новой
 *  ревизии. */
export function ReminderNote() {
  return (
    <Warn>
      Позиции по документу действуют больше 30 дней, а ревизия РД не выдана. Проверьте, нужны ли они
      ещё, или отзовите с причиной.
    </Warn>
  );
}
